import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-738">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-739">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-740">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-741">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-742" /> : <Plus className="w-4 h-4" data-id="element-743" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-744">
        <p className="text-gray-600 leading-relaxed" data-id="element-745">{answer}</p>
      </div>
    </div>;
}
export function NhsVsPrivateArticle() {
  const {
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pick = (en: string, ua: string, ru: string, ro: string) => {
    if (lang === 'ua') return ua;
    if (lang === 'ru') return ru;
    if (lang === 'ro') return ro;
    return en;
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: pick('Is NHS dentistry free?', 'Чи безкоштовна NHS стоматологія?', 'Бесплатна ли NHS стоматология?', 'Este stomatologia NHS gratuită?'),
    a: pick('No, for most adults it is paid, but subsidized. Only children, pregnant women, mothers of infants, and people with certain benefits are treated for free.', 'Ні, для більшості дорослих вона платна, але субсидована. Безкоштовно лікуються лише діти, вагітні, матері немовлят та люди з певними пільгами (benefits).', 'Нет, для большинства взрослых она платная, но субсидируемая. Бесплатно лечатся только дети, беременные, матери младенцев и люди с определенными льготами (benefits).', 'Nu, pentru majoritatea adulților este plătită, dar subvenționată. Doar copiii, femeile însărcinate, mamele de sugari și persoanele cu anumite beneficii sunt tratate gratuit.')
  }, {
    q: pick('Can I go to a private doctor if I am registered with the NHS?', 'Чи можу я піти до приватного лікаря, якщо я зареєстрований в NHS?', 'Могу ли я пойти к частному врачу, если я зарегистрирован в NHS?', 'Pot merge la un medic privat dacă sunt înregistrat la NHS?'),
    a: pick('Yes, you are free to choose between NHS and private medicine. Registration with the NHS does not restrict you from visiting private clinics.', 'Так, ви можете вільно обирати між NHS та приватною медициною. Реєстрація в NHS не обмежує вас у відвідуванні приватних клінік.', 'Да, вы можете свободно выбирать между NHS и частной медициной. Регистрация в NHS не ограничивает вас в посещении частных клиник.', 'Da, sunteți liber să alegeți între NHS și medicina privată. Înregistrarea la NHS nu vă restricționează să vizitați clinici private.')
  }, {
    q: pick('Why are the queues in the NHS so long?', 'Чому в NHS такі довгі черги?', 'Почему в NHS такие длинные очереди?', 'De ce sunt cozile la NHS atât de lungi?'),
    a: pick('Due to high workload and lack of funding. Many NHS clinics do not accept new patients, and waiting times for an appointment can be months.', 'Через велике навантаження та нестачу фінансування. Багато клінік NHS не приймають нових пацієнтів, а час очікування на прийом може складати місяці.', 'Из-за большой нагрузки и нехватки финансирования. Многие клиники NHS не принимают новых пациентов, а время ожидания на прием может составлять месяцы.', 'Din cauza volumului mare de muncă și a lipsei de finanțare. Multe clinici NHS nu acceptă pacienți noi, iar timpii de așteptare pentru o programare pot fi de luni de zile.')
  }];
  return <Layout data-id="element-746">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-747" />

      <article className="bg-white" data-id="element-748">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-749">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-750">
            <Link to={`/${lang}/blog`} className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-751">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-752" />
              {pick('Back to blog', 'Назад до блогу', 'Назад в блог', 'Înapoi la blog')}
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-753">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-754">
                <Tag className="w-3 h-3 mr-2" data-id="element-755" />
                {pick('Patient Guide', 'Гід пацієнта', 'Гид пациента', 'Ghidul Pacientului')}
              </span>
              <span className="flex items-center" data-id="element-756">
                <Calendar className="w-4 h-4 mr-2" data-id="element-757" />
                {pick('February 2026', 'Лютий 2026', 'Февраль 2026', 'Februarie 2026')}
              </span>
              <span className="flex items-center" data-id="element-758">
                <Clock className="w-4 h-4 mr-2" data-id="element-759" />
                {pick('10 min read', '10 хв читання', '10 мин чтения', '10 min citire')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-760">
              {pick('NHS vs Private Dentistry in the UK — Which to choose?', 'NHS vs приватна стоматологія у Великобританії — що обрати?', 'NHS vs частная стоматология в Великобритании — что выбрать?', 'NHS vs Stomatologie Privată în Marea Britanie — Ce să alegeți?')}
            </h1>

            <div className="flex items-center" data-id="element-761">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-762">
                DC
              </div>
              <div data-id="element-763">
                <p className="font-medium text-gray-900" data-id="element-764">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-765">
                  {pick('Expert Article', 'Експертна стаття', 'Экспертная статья', 'Articol Expert')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-766">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-767">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-768">
              {pick("For many people who have moved to the UK, the local healthcare system can seem confusing. This is especially true for dentistry, where there is a clear division between state (NHS) and private (Private). What to choose? Where is it better to treat teeth? Let's break down the details.", "Для багатьох українців, які переїхали до Великобританії, місцева система охорони здоров'я може здаватися заплутаною. Особливо це стосується стоматології, де існує чіткий поділ на державну (NHS) та приватну (Private). Що обрати? Де краще лікувати зуби? Розбираємося в деталях.", 'Для многих людей, переехавших в Великобританию, местная система здравоохранения может казаться запутанной. Особенно это касается стоматологии, где существует четкое разделение на государственную (NHS) и частную (Private). Что выбрать? Где лучше лечить зубы? Разбираемся в деталях.', 'Pentru mulți oameni care s-au mutat în Marea Britanie, sistemul local de sănătate poate părea confuz. Acest lucru este valabil mai ales pentru stomatologie, unde există o diviziune clară între stat (NHS) și privat (Private). Ce să alegeți? Unde este mai bine să tratați dinții? Să detaliem.')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-769">
              {pick('What is NHS dentistry?', 'Що таке NHS стоматологія?', 'Что такое NHS стоматология?', 'Ce este stomatologia NHS?')}
            </h2>
            <p className="mb-6" data-id="element-770">
              {pick('NHS (National Health Service) is the state healthcare system. Dentistry under the NHS is not completely free for most adults, but prices are fixed and subsidized by the state.', "NHS (National Health Service) — це державна система охорони здоров'я. Стоматологія в рамках NHS не є повністю безкоштовною для більшості дорослих, але ціни тут фіксовані та субсидовані державою.", 'NHS (National Health Service) — это государственная система здравоохранения. Стоматология в рамках NHS не является полностью бесплатной для большинства взрослых, но цены здесь фиксированы и субсидируются государством.', 'NHS (National Health Service) este sistemul de sănătate de stat. Stomatologia sub NHS nu este complet gratuită pentru majoritatea adulților, dar prețurile sunt fixe și subvenționate de stat.')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-771">
              {pick('Problems with NHS dentistry', 'Проблеми NHS стоматології', 'Проблемы NHS стоматологии', 'Probleme cu stomatologia NHS')}
            </h2>
            <ul className="space-y-2 mb-8" data-id="element-772">
              <li className="flex items-start" data-id="element-773">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-774" />
                <span data-id="element-775">
                  <strong data-id="element-776">
                    {pick('Long queues:', 'Довгі черги:', 'Длинные очереди:', 'Cozi lungi:')}
                  </strong>{' '}
                  {pick('Waiting for a routine appointment can take months.', 'Очікування на плановий прийом може тривати місяці.', 'Ожидание планового приема может длиться месяцы.', 'Așteptarea pentru o programare de rutină poate dura luni de zile.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-777">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-778" />
                <span data-id="element-779">
                  <strong data-id="element-780">
                    {pick('Registration:', 'Реєстрація:', 'Регистрация:', 'Înregistrare:')}
                  </strong>{' '}
                  {pick('Finding a clinic that accepts new NHS patients is currently very difficult.', 'Знайти клініку, яка приймає нових NHS пацієнтів, зараз дуже складно.', 'Найти клинику, которая принимает новых NHS пациентов, сейчас очень сложно.', 'Găsirea unei clinici care acceptă noi pacienți NHS este în prezent foarte dificilă.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-781">
                <XCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" data-id="element-782" />
                <span data-id="element-783">
                  <strong data-id="element-784">
                    {pick('Material limitations:', 'Обмеження матеріалів:', 'Ограничения материалов:', 'Limitări ale materialelor:')}
                  </strong>{' '}
                  {pick('The NHS uses basic materials (e.g., silver amalgam fillings on back teeth instead of white ones).', 'NHS використовує базові матеріали (наприклад, срібні амальгамні пломби на жувальні зуби замість білих).', 'NHS использует базовые материалы (например, серебряные амальгамные пломбы на жевательные зубы вместо белых).', 'NHS folosește materiale de bază (de exemplu, plombe de amalgam de argint pe dinții din spate în loc de cele albe).')}
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-785">
              {pick('Private dentistry: advantages', 'Приватна стоматологія: переваги', 'Частная стоматология: преимущества', 'Stomatologie privată: avantaje')}
            </h2>
            <ul className="space-y-3 mb-8" data-id="element-786">
              <li className="flex items-start" data-id="element-787">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-788" />
                <span data-id="element-789">
                  <strong data-id="element-790">
                    {pick('Speed:', 'Швидкість:', 'Скорость:', 'Viteză:')}
                  </strong>{' '}
                  {pick('Appointments can be made on the same day or the next.', 'Прийом можна отримати в той же день або на наступний.', 'Прием можно получить в тот же день или на следующий.', 'Programările pot fi făcute în aceeași zi sau a doua zi.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-791">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-792" />
                <span data-id="element-793">
                  <strong data-id="element-794">
                    {pick('Aesthetics:', 'Естетика:', 'Эстетика:', 'Estetică:')}
                  </strong>{' '}
                  {pick('Modern photopolymer (white) fillings, ceramics, and veneers are used.', 'Використовуються сучасні фотополімерні (білі) пломби, кераміка, вініри.', 'Используются современные фотополимерные (белые) пломбы, керамика, виниры.', 'Se folosesc plombe fotopolimerice moderne (albe), ceramică și fațete.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-795">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-796" />
                <span data-id="element-797">
                  <strong data-id="element-798">
                    {pick('Equipment:', 'Обладнання:', 'Оборудование:', 'Echipament:')}
                  </strong>{' '}
                  {pick('Digital scanners, microscopes, CT scans — this is the standard for a good private clinic.', 'Цифрові сканери, мікроскопи, КТ — це стандарт для хорошої приватної клініки.', 'Цифровые сканеры, микроскопы, КТ — это стандарт для хорошей частной клиники.', 'Scanere digitale, microscoape, scanări CT — acesta este standardul pentru o clinică privată bună.')}
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-799">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-800">
              {pick('Frequently Asked Questions', 'Часті запитання', 'Частые вопросы', 'Întrebări Frecvente')}
            </h3>
            <div className="space-y-2" data-id="element-801">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-802" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-803">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-804">
              {pick('Read Also', 'Читайте також', 'Читайте также', 'Citește și')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-805">
              <Link to={`/${lang}/blog/dental-treatment-uk-without-insurance`} data-id="element-806">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-807">
                  <span className="text-teal-600 font-medium" data-id="element-808">
                    {pick('Dentistry Without Insurance', 'Лікування без страховки', 'Лечение без страховки', 'Stomatologie Fără Asigurare')}
                  </span>
                </Card>
              </Link>
              <Link to={`/${lang}/prices`} data-id="element-809">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-810">
                  <span className="text-teal-600 font-medium" data-id="element-811">
                    {pick('Our Prices', 'Наші ціни', 'Наши цены', 'Prețurile Noastre')}
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-812">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-813">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-814">
              {pick('Choose quality dentistry without compromise', 'Оберіть якісну стоматологію без компромісів', 'Выберите качественную стоматологию без компромиссов', 'Alegeți stomatologie de calitate fără compromisuri')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-815">
              {pick('Book a consultation at Dentico Clinic and receive European-level treatment.', 'Запишіться на консультацію в Dentico Clinic та отримайте лікування європейського рівня.', 'Запишитесь на консультацию в Dentico Clinic и получите лечение европейского уровня.', 'Programați o consultație la Dentico Clinic și primiți tratament la nivel european.')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-816">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-817">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-818">
                  <Calendar className="w-5 h-5" data-id="element-819" />
                  {pick('Book Online', 'Записатись онлайн', 'Записаться онлайн', 'Programare Online')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-820">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-821">
                  <MessageCircle className="w-5 h-5" data-id="element-822" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}